import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, ButtonBase, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

import { sortGroups } from '../menuEditorPayloads';
import GroupEditor, { GroupSettingsForm, groupFormErrors, groupFormFrom } from './GroupEditor';

/** How many dishes (or their sizes) use each group. */
const usageByGroup = (categories) => {
  const usage = new Map();
  (categories || []).forEach((category) =>
    (category.items || []).forEach((item) => {
      const ids = new Set([
        ...(item.groups || []).map((ref) => ref.id),
        ...(item.sizes || []).flatMap((size) => (size.groups || []).map((ref) => ref.id)),
      ]);
      ids.forEach((id) => usage.set(id, (usage.get(id) || 0) + 1));
    })
  );
  return usage;
};

const matchesGroup = (group, query) => {
  if (!query) {
    return true;
  }
  const q = query.toLowerCase();
  return (
    (group.name || '').toLowerCase().includes(q) ||
    (group.options || []).some((option) => (option.name || '').toLowerCase().includes(q))
  );
};

const NewGroupPanel = ({ actions, onCancel, onCreated }) => {
  const [form, setForm] = useState(groupFormFrom(null));
  const [saving, setSaving] = useState(false);
  const errors = groupFormErrors(form);

  const create = async () => {
    setSaving(true);
    try {
      if (await actions.createGroup(form)) {
        onCreated(form.name);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ height: '100%', overflowY: 'auto', px: 3, py: 2.5 }} component="section" aria-label="New choice group">
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
        New choice group
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add the choices after creating it, then attach the group to dishes from each dish&apos;s Choices tab.
      </Typography>
      <GroupSettingsForm form={form} onChange={setForm} errors={errors} isNew />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={create} disabled={saving || Object.keys(errors).length > 0}>
          Create group
        </Button>
      </Box>
    </Paper>
  );
};

NewGroupPanel.propTypes = {
  actions: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

const GroupsPane = ({ groups, categories, menuId, currencySymbol, showIds, actions }) => {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pendingName, setPendingName] = useState(null);

  const ordered = useMemo(() => sortGroups(groups), [groups]);
  const visible = ordered.filter((group) => matchesGroup(group, query.trim()));
  const usage = useMemo(() => usageByGroup(categories), [categories]);
  const selected = ordered.find((group) => group.id === selectedId) || null;

  // Select the first group, and after creating one, select it once it shows up.
  useEffect(() => {
    if (pendingName) {
      const created = [...ordered].reverse().find((group) => group.name === pendingName);
      if (created) {
        setSelectedId(created.id);
        setPendingName(null);
      }
      return;
    }
    if (!creating && !selected && ordered.length > 0) {
      setSelectedId(ordered[0].id);
    }
  }, [ordered, selected, creating, pendingName]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2, height: '100%', minHeight: 0 }}>
      <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }} component="nav" aria-label="Choice groups">
        <Box sx={{ p: 1.5, display: 'flex', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small"
            placeholder="Search groups or choices"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Search choice groups' }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setCreating(true);
              setSelectedId(null);
            }}
            aria-label="New choice group"
            sx={{ minWidth: 0, px: 1.5 }}
          >
            <AddIcon />
          </Button>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
          {visible.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              {groups.length === 0 ? 'No choice groups yet.' : 'No groups match.'}
            </Typography>
          )}
          {visible.map((group) => {
            const active = !creating && group.id === selectedId;
            const count = (group.options || []).length;
            const used = usage.get(group.id) || 0;
            return (
              <ButtonBase
                key={group.id}
                onClick={() => {
                  setCreating(false);
                  setSelectedId(group.id);
                }}
                aria-current={active ? 'true' : undefined}
                sx={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  mb: 0.25,
                  bgcolor: active ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: active ? 'action.selected' : 'action.hover' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: active ? 600 : 500 }}>
                  {group.name || 'No name'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {group.required ? 'Required' : 'Optional'} · {count} choice{count === 1 ? '' : 's'} · {used ? `${used} dish${used === 1 ? '' : 'es'}` : 'unused'}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Paper>
      <Box sx={{ minHeight: 0 }}>
        {creating ? (
          <NewGroupPanel
            actions={actions}
            onCancel={() => setCreating(false)}
            onCreated={(name) => {
              setCreating(false);
              setPendingName(name);
            }}
          />
        ) : selected ? (
          <GroupEditor
            group={selected}
            menuId={menuId}
            categories={categories}
            usedBy={usage.get(selected.id) || 0}
            currencySymbol={currencySymbol}
            showIds={showIds}
            actions={actions}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <Paper variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Typography color="text.secondary">Choice groups hold the options customers pick from, like sauces or sides.</Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

GroupsPane.propTypes = {
  groups: PropTypes.array.isRequired,
  categories: PropTypes.array.isRequired,
  menuId: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  showIds: PropTypes.bool,
  actions: PropTypes.object.isRequired,
};

export default GroupsPane;
